// shared/roomTx.js
// クイズ ワン ワード用のFirestoreトランザクション

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  runTransaction,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  FIVE_LEAGUE,
  FL_PHASE,
  FL_ROOM_STATUS,
  FL_ROLE,
  FL_GAME_MODE,
  generateRoomCode,
  getDefaultRoomConfig,
  normalizeChar,
} from './fiveLeague';
import { generateQuestionSet, getAllQuestions } from '../constants/questions';

// ═══════════════════════════════════════════════════════════
// ルーム作成
// ═══════════════════════════════════════════════════════════

/**
 * クイズ ワン ワードルームを作成
 * @param {Object} params
 * @param {string} params.hostId - ホストのプレイヤーID
 * @param {string} params.nickname - ホストのニックネーム
 * @param {string} params.role - プレイヤーロール
 * @param {string} params.gameMode - ゲームモード ('oneword' | 'chain')
 * @param {Object} params.config - ルーム設定
 */
export async function createRoom({ hostId, nickname, role = FL_ROLE.CONTROLLER, gameMode = FL_GAME_MODE.ONEWORD, config = {} }) {
  try {
    if (!hostId || !nickname) {
      return { ok: false, reason: 'bad-args' };
    }

    const roomCode = generateRoomCode();
    const roomId = `fl_${roomCode}_${Date.now()}`;
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const roomConfig = { ...getDefaultRoomConfig(), ...config };
    const now = Date.now();

    const roomData = {
      roomId,
      roomCode,
      hostId,
      gameMode, // 追加: ゲームモード
      status: FL_ROOM_STATUS.WAITING,
      players: [
        {
          playerId: hostId,
          nickname: nickname.slice(0, FIVE_LEAGUE.NICKNAME_MAX_LENGTH),
          role,
          ready: false,
          connected: true,
          joinedAt: now,
        },
      ],
      playerOrder: [hostId],
      config: roomConfig,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(ref, roomData);

    return { ok: true, roomId, roomCode };
  } catch (e) {
    console.error('[createRoom]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// ルーム検索・参加
// ═══════════════════════════════════════════════════════════

/**
 * ルームコードでルームを検索
 */
export async function findRoomByCode(roomCode) {
  try {
    if (!roomCode || roomCode.length !== 6) {
      return { ok: false, reason: 'invalid-code' };
    }

    const q = query(
      collection(db, 'fiveLeagueRooms'),
      where('roomCode', '==', roomCode.toUpperCase()),
      where('status', '==', FL_ROOM_STATUS.WAITING)
    );

    const snap = await getDocs(q);
    if (snap.empty) {
      return { ok: false, reason: 'not-found' };
    }

    const roomDoc = snap.docs[0];
    return { ok: true, roomId: roomDoc.id, room: roomDoc.data() };
  } catch (e) {
    console.error('[findRoomByCode]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * ルームに参加
 */
export async function joinRoom(roomId, { playerId, nickname, role = FL_ROLE.CONTROLLER }) {
  try {
    if (!roomId || !playerId || !nickname) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('not-found');
      }

      const room = snap.data();

      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('game-started');
      }

      const players = room.players || [];
      if (players.length >= FIVE_LEAGUE.MAX_PLAYERS) {
        throw new Error('room-full');
      }

      const existing = players.find(p => p.playerId === playerId);
      if (existing) {
        const updatedPlayers = players.map(p =>
          p.playerId === playerId
            ? { ...p, connected: true, nickname: nickname.slice(0, FIVE_LEAGUE.NICKNAME_MAX_LENGTH) }
            : p
        );
        tx.update(ref, {
          players: updatedPlayers,
          updatedAt: serverTimestamp(),
        });
        return { ok: true, reconnected: true };
      }

      const newPlayer = {
        playerId,
        nickname: nickname.slice(0, FIVE_LEAGUE.NICKNAME_MAX_LENGTH),
        role,
        ready: false,
        connected: true,
        joinedAt: Date.now(),
      };

      tx.update(ref, {
        players: [...players, newPlayer],
        playerOrder: [...(room.playerOrder || []), playerId],
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });

    return result;
  } catch (e) {
    console.error('[joinRoom]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// Ready状態・ゲーム開始
// ═══════════════════════════════════════════════════════════

/**
 * Readyを切り替え
 */
export async function setReady(roomId, playerId, ready) {
  try {
    if (!roomId || !playerId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('game-started');
      }

      const players = (room.players || []).map(p =>
        p.playerId === playerId ? { ...p, ready } : p
      );

      tx.update(ref, { players, updatedAt: serverTimestamp() });
    });

    return { ok: true };
  } catch (e) {
    console.error('[setReady]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * ゲームを開始
 */
export async function startGame(roomId, hostId) {
  try {
    if (!roomId || !hostId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();

      if (room.hostId !== hostId) {
        throw new Error('not-host');
      }

      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('already-started');
      }

      const players = room.players || [];
      const controllers = players.filter(p => p.role !== FL_ROLE.DISPLAY && p.connected);

      if (controllers.length < FIVE_LEAGUE.MIN_PLAYERS) {
        throw new Error('not-enough-players');
      }
      if (controllers.length > FIVE_LEAGUE.MAX_PLAYERS) {
        throw new Error('too-many-players');
      }

      const allReady = controllers.every(p => p.ready);
      if (!allReady) {
        throw new Error('not-all-ready');
      }

      // プレイヤー順序をシャッフル
      const playerOrder = controllers.map(p => p.playerId);
      for (let i = playerOrder.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [playerOrder[i], playerOrder[j]] = [playerOrder[j], playerOrder[i]];
      }

      // slotIndex割り当て
      const updatedPlayers = players.map(p => {
        const slotIndex = playerOrder.indexOf(p.playerId);
        return slotIndex >= 0 ? { ...p, slotIndex } : p;
      });

      // 問題セット生成
      const questionSet = generateQuestionSet(controllers.length, room.config?.maxQuestions || 5);
      if (questionSet.length === 0) {
        throw new Error('no-questions');
      }

      const firstQuestion = questionSet[0];
      const config = room.config || getDefaultRoomConfig();
      const now = Date.now();

      const gameState = {
        questionIndex: 0,
        correctQuestions: 0,
        phase: FL_PHASE.READING,
        questionSet: questionSet.map(q => q.id),
        current: {
          questionId: firstQuestion.id,
          category: firstQuestion.category,
          script: firstQuestion.script,
          length: firstQuestion.length,
          prompt: firstQuestion.prompt,
          answer: firstQuestion.answer,
          reading: firstQuestion.reading || null, // 読み仮名（TTSで正しく読むため）
          readingStartAt: now,
          answerStartAt: null,
          answerEndAt: null,
          submissions: playerOrder.map((pid, idx) => ({
            playerId: pid,
            index: idx,
            candidates: null,
            chosen: null,
            confirmedAt: null,
          })),
          revealed: null,
        },
      };

      tx.update(ref, {
        status: FL_ROOM_STATUS.PLAYING,
        players: updatedPlayers,
        playerOrder,
        game: gameState,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });

    return result;
  } catch (e) {
    console.error('[startGame]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 問題読み上げ完了→回答開始
// ═══════════════════════════════════════════════════════════

/**
 * 問題読み上げ完了後、回答フェーズを開始
 */
export async function startAnswering(roomId) {
  try {
    if (!roomId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      const game = room.game;
      if (!game || game.phase !== FL_PHASE.READING) {
        throw new Error('not-reading');
      }

      const config = room.config || getDefaultRoomConfig();
      const now = Date.now();

      tx.update(ref, {
        'game.phase': FL_PHASE.ANSWERING,
        'game.current.answerStartAt': now,
        'game.current.answerEndAt': now + config.answerTimeSec * 1000,
        updatedAt: serverTimestamp(),
      });
    });

    return { ok: true };
  } catch (e) {
    console.error('[startAnswering]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 回答確定
// ═══════════════════════════════════════════════════════════

/**
 * 回答を確定
 */
export async function confirmAnswer(roomId, playerId, chosen) {
  try {
    if (!roomId || !playerId || !chosen) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      const game = room.game;
      if (!game || game.phase !== FL_PHASE.ANSWERING) {
        throw new Error('not-answering');
      }

      const current = game.current;
      const now = Date.now();

      const normalizedChosen = normalizeChar(chosen, current.script);

      const submissions = (current.submissions || []).map(s =>
        s.playerId === playerId
          ? { ...s, chosen: normalizedChosen, confirmedAt: now }
          : s
      );

      const allConfirmed = submissions.every(s => s.confirmedAt != null);

      const updates = {
        'game.current.submissions': submissions,
        updatedAt: serverTimestamp(),
      };

      if (allConfirmed) {
        const config = room.config || getDefaultRoomConfig();
        updates['game.phase'] = FL_PHASE.REVEAL_COUNTDOWN;
        updates['game.current.revealStartAt'] = now;
        updates['game.current.revealOpenAt'] = now + config.revealCountdownSec * 1000;
      }

      tx.update(ref, updates);

      return { ok: true, allConfirmed };
    });

    return result;
  } catch (e) {
    console.error('[confirmAnswer]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// タイムアウト・結果オープン
// ═══════════════════════════════════════════════════════════

/**
 * 回答時間終了
 */
export async function handleAnswerTimeout(roomId) {
  try {
    if (!roomId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      const game = room.game;
      if (!game || game.phase !== FL_PHASE.ANSWERING) {
        throw new Error('not-answering');
      }

      const now = Date.now();
      const config = room.config || getDefaultRoomConfig();

      tx.update(ref, {
        'game.phase': FL_PHASE.REVEAL_COUNTDOWN,
        'game.current.revealStartAt': now,
        'game.current.revealOpenAt': now + config.revealCountdownSec * 1000,
        updatedAt: serverTimestamp(),
      });
    });

    return { ok: true };
  } catch (e) {
    console.error('[handleAnswerTimeout]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 結果をオープン
 */
export async function revealAnswers(roomId) {
  try {
    if (!roomId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      const game = room.game;
      if (!game || game.phase !== FL_PHASE.REVEAL_COUNTDOWN) {
        throw new Error('not-countdown');
      }

      const current = game.current;
      const answer = current.answer || [];
      const submissions = current.submissions || [];

      const letters = submissions.map(s => s.chosen || '');
      const script = current.script;
      const correctMask = submissions.map((s, i) => {
        // 正解データも正規化して比較（半角/全角、大文字/小文字を統一）
        const expected = normalizeChar(answer[i] || '', script);
        const actual = s.chosen || ''; // 既に confirmAnswer で正規化済み
        return expected.trim() === '' || actual === expected;
      });

      const isPerfect = correctMask.every(c => c);
      const allConfirmed = submissions.every(s => s.confirmedAt != null);

      const revealed = {
        letters,
        correctMask,
        answerWord: answer,
        answerReading: current.reading || null, // 読み仮名（TTSで正しく読むため）
        isPerfect,
        endedBy: allConfirmed ? 'all_confirmed' : 'timeout',
      };

      const now = Date.now();
      const config = room.config || getDefaultRoomConfig();

      tx.update(ref, {
        'game.phase': FL_PHASE.REVEALED,
        'game.current.revealed': revealed,
        'game.current.solutionAt': now + config.revealHoldSec * 1000,
        updatedAt: serverTimestamp(),
      });

      return { ok: true, revealed };
    });

    return result;
  } catch (e) {
    console.error('[revealAnswers]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 正解表示・次の問題
// ═══════════════════════════════════════════════════════════

/**
 * 正解表示
 */
export async function showSolution(roomId) {
  try {
    if (!roomId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      const game = room.game;
      if (!game || game.phase !== FL_PHASE.REVEALED) {
        throw new Error('not-revealed');
      }

      const now = Date.now();
      const config = room.config || getDefaultRoomConfig();

      tx.update(ref, {
        'game.phase': FL_PHASE.SOLUTION,
        'game.current.nextAt': now + config.solutionHoldSec * 1000,
        updatedAt: serverTimestamp(),
      });
    });

    return { ok: true };
  } catch (e) {
    console.error('[showSolution]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 次の問題へ or ラウンド終了
 */
export async function advanceToNext(roomId) {
  try {
    if (!roomId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      const game = room.game;
      if (!game || game.phase !== FL_PHASE.SOLUTION) {
        throw new Error('not-solution');
      }

      const current = game.current;
      const revealed = current.revealed;
      const isPerfect = revealed?.isPerfect || false;

      const questionIndex = game.questionIndex;
      const correctQuestions = game.correctQuestions + (isPerfect ? 1 : 0);
      const maxQuestions = room.config?.maxQuestions || 5;

      // 問題履歴を保存
      const history = game.history || [];
      const historyEntry = {
        prompt: current.prompt,
        category: current.category,
        script: current.script,
        letters: revealed?.letters || [],
        correctMask: revealed?.correctMask || [],
        answer: current.answer || [],
        isPerfect,
      };
      const newHistory = [...history, historyEntry];

      const shouldEnd = !isPerfect || questionIndex >= maxQuestions - 1;

      if (shouldEnd) {
        tx.update(ref, {
          status: FL_ROOM_STATUS.FINISHED,
          'game.phase': FL_PHASE.END_ROUND,
          'game.correctQuestions': correctQuestions,
          'game.history': newHistory,
          updatedAt: serverTimestamp(),
        });
        return { ok: true, finished: true };
      }

      const nextIndex = questionIndex + 1;
      const questionSet = game.questionSet || [];
      const nextQuestionId = questionSet[nextIndex];

      const nextQuestion = getAllQuestions().find(q => q.id === nextQuestionId);

      if (!nextQuestion) {
        throw new Error('question-not-found');
      }

      const config = room.config || getDefaultRoomConfig();
      const now = Date.now();
      const playerOrder = room.playerOrder || [];

      const newCurrent = {
        questionId: nextQuestion.id,
        category: nextQuestion.category,
        script: nextQuestion.script,
        length: nextQuestion.length,
        prompt: nextQuestion.prompt,
        answer: nextQuestion.answer,
        reading: nextQuestion.reading || null, // 読み仮名（TTSで正しく読むため）
        readingStartAt: now,
        answerStartAt: null,
        answerEndAt: null,
        submissions: playerOrder.map((pid, idx) => ({
          playerId: pid,
          index: idx,
          candidates: null,
          chosen: null,
          confirmedAt: null,
        })),
        revealed: null,
      };

      tx.update(ref, {
        'game.questionIndex': nextIndex,
        'game.correctQuestions': correctQuestions,
        'game.phase': FL_PHASE.READING,
        'game.current': newCurrent,
        'game.history': newHistory,
        updatedAt: serverTimestamp(),
      });

      return { ok: true, finished: false };
    });

    return result;
  } catch (e) {
    console.error('[advanceToNext]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 再戦・クイックマッチ
// ═══════════════════════════════════════════════════════════

/**
 * 同じメンバーで再戦
 */
export async function rematch(roomId, hostId) {
  try {
    if (!roomId || !hostId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('not-found');
      }

      const room = snap.data();

      if (room.hostId !== hostId) {
        throw new Error('not-host');
      }

      if (room.status !== FL_ROOM_STATUS.FINISHED) {
        throw new Error('not-finished');
      }

      // プレイヤーのready状態をリセット（slotIndexを削除）
      const players = (room.players || []).map(p => {
        const { slotIndex, ...rest } = p;
        return {
          ...rest,
          ready: false,
        };
      });

      // playerOrderもプレイヤーIDのリストにリセット（startGameで再シャッフルされる）
      const playerOrder = players.map(p => p.playerId);

      tx.update(ref, {
        status: FL_ROOM_STATUS.WAITING,
        players,
        playerOrder,
        game: deleteField(),
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });

    return result;
  } catch (e) {
    console.error('[rematch]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 空いている部屋を探す（フリー対戦用）
 */
export async function findAvailableRoom() {
  try {
    const q = query(
      collection(db, 'fiveLeagueRooms'),
      where('status', '==', FL_ROOM_STATUS.WAITING)
    );

    const snap = await getDocs(q);

    // 空いている部屋を探す（プレイヤー数がMAX未満）
    for (const roomDoc of snap.docs) {
      const room = roomDoc.data();
      const players = room.players || [];
      if (players.length < FIVE_LEAGUE.MAX_PLAYERS) {
        return { ok: true, roomId: roomDoc.id, room };
      }
    }

    return { ok: false, reason: 'no-room' };
  } catch (e) {
    console.error('[findAvailableRoom]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * クイックマッチ（空いている部屋に入るか、新規作成）
 */
export async function quickMatch({ playerId, nickname, role = FL_ROLE.CONTROLLER }) {
  try {
    if (!playerId || !nickname) {
      return { ok: false, reason: 'bad-args' };
    }

    // 1. 空いている部屋を探す
    const findResult = await findAvailableRoom();

    if (findResult.ok) {
      // 2. 見つかったら参加
      const joinResult = await joinRoom(findResult.roomId, { playerId, nickname, role });
      if (joinResult.ok) {
        return { ok: true, roomId: findResult.roomId, created: false };
      }
    }

    // 3. 見つからなければ新規作成
    const createResult = await createRoom({ hostId: playerId, nickname, role });
    if (createResult.ok) {
      return { ok: true, roomId: createResult.roomId, roomCode: createResult.roomCode, created: true };
    }

    return { ok: false, reason: createResult.reason };
  } catch (e) {
    console.error('[quickMatch]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// ルーム退出・購読
// ═══════════════════════════════════════════════════════════

/**
 * ルームから退出
 */
export async function leaveRoom(roomId, playerId) {
  try {
    if (!roomId || !playerId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('not-found');

      const room = snap.data();
      const players = room.players || [];
      const remaining = players.filter(p => p.playerId !== playerId);

      if (remaining.length === 0) {
        tx.delete(ref);
        return { ok: true, roomDeleted: true };
      }

      let newHostId = room.hostId;
      if (room.hostId === playerId) {
        newHostId = remaining[0].playerId;
      }

      tx.update(ref, {
        players: remaining,
        playerOrder: (room.playerOrder || []).filter(id => id !== playerId),
        hostId: newHostId,
        updatedAt: serverTimestamp(),
      });

      return { ok: true, roomDeleted: false };
    });

    return result;
  } catch (e) {
    console.error('[leaveRoom]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * ルームを購読
 */
export function subscribeRoom(roomId, callback) {
  if (!roomId) return () => {};

  const ref = doc(db, 'fiveLeagueRooms', roomId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      callback(snap.data());
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('[subscribeRoom] Error:', error);
    callback(null);
  });
}

/**
 * ルームを取得
 */
export async function getRoom(roomId) {
  try {
    if (!roomId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'fiveLeagueRooms', roomId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return { ok: false, reason: 'not-found' };
    }

    return { ok: true, room: snap.data() };
  } catch (e) {
    console.error('[getRoom]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 管理者用：ルーム管理
// ═══════════════════════════════════════════════════════════

/**
 * 全ルームを取得（管理者用）
 */
export async function getAllRooms() {
  try {
    const q = query(collection(db, 'fiveLeagueRooms'));
    const snap = await getDocs(q);

    const rooms = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
    }));

    return { ok: true, rooms };
  } catch (e) {
    console.error('[getAllRooms]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 古いルームを削除（管理者用）
 * @param {number} maxAgeHours - 何時間以上古いルームを削除するか（デフォルト: 24時間）
 */
export async function cleanupOldRooms(maxAgeHours = 24) {
  try {
    const q = query(collection(db, 'fiveLeagueRooms'));
    const snap = await getDocs(q);

    const now = Date.now();
    const cutoff = now - maxAgeHours * 60 * 60 * 1000;
    let deletedCount = 0;

    for (const roomDoc of snap.docs) {
      const room = roomDoc.data();

      // createdAtがTimestamp型の場合とミリ秒の場合を両方サポート
      let createdAtMs;
      if (room.createdAt?.toMillis) {
        createdAtMs = room.createdAt.toMillis();
      } else if (room.createdAt?.seconds) {
        createdAtMs = room.createdAt.seconds * 1000;
      } else {
        // roomIdからタイムスタンプを抽出（fl_XXXX_timestamp形式）
        const parts = roomDoc.id.split('_');
        createdAtMs = parseInt(parts[2]) || 0;
      }

      if (createdAtMs < cutoff) {
        await deleteDoc(doc(db, 'fiveLeagueRooms', roomDoc.id));
        deletedCount++;
      }
    }

    return { ok: true, deletedCount };
  } catch (e) {
    console.error('[cleanupOldRooms]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 非アクティブなルームを削除（管理者用）
 * updatedAtを基準に、一定時間操作がないルームを削除
 * @param {number} inactiveMinutes - 何分間操作がないルームを削除するか（デフォルト: 30分）
 */
export async function cleanupInactiveRooms(inactiveMinutes = 30) {
  try {
    const q = query(collection(db, 'fiveLeagueRooms'));
    const snap = await getDocs(q);

    const now = Date.now();
    const cutoff = now - inactiveMinutes * 60 * 1000;
    let deletedCount = 0;
    const deletedRooms = [];

    for (const roomDoc of snap.docs) {
      const room = roomDoc.data();

      // updatedAtを取得（Timestamp型とミリ秒の両方をサポート）
      let updatedAtMs;
      if (room.updatedAt?.toMillis) {
        updatedAtMs = room.updatedAt.toMillis();
      } else if (room.updatedAt?.seconds) {
        updatedAtMs = room.updatedAt.seconds * 1000;
      } else if (room.createdAt?.toMillis) {
        // updatedAtがない場合はcreatedAtを使用
        updatedAtMs = room.createdAt.toMillis();
      } else if (room.createdAt?.seconds) {
        updatedAtMs = room.createdAt.seconds * 1000;
      } else {
        // roomIdからタイムスタンプを抽出（fl_XXXX_timestamp形式）
        const parts = roomDoc.id.split('_');
        updatedAtMs = parseInt(parts[2]) || 0;
      }

      if (updatedAtMs < cutoff) {
        await deleteDoc(doc(db, 'fiveLeagueRooms', roomDoc.id));
        deletedCount++;
        deletedRooms.push({
          roomId: roomDoc.id,
          roomCode: room.roomCode,
          status: room.status,
          playerCount: room.players?.length || 0,
          inactiveMinutes: Math.round((now - updatedAtMs) / 60000),
        });
      }
    }

    return { ok: true, deletedCount, deletedRooms };
  } catch (e) {
    console.error('[cleanupInactiveRooms]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 指定したルームを削除（管理者用）
 */
export async function deleteRoom(roomId) {
  try {
    if (!roomId) {
      return { ok: false, reason: 'bad-args' };
    }

    await deleteDoc(doc(db, 'fiveLeagueRooms', roomId));
    return { ok: true };
  } catch (e) {
    console.error('[deleteRoom]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}
